import { slugify } from "@/shared/utils";

/** `CreateCatalogSuperCategoryTreeDto` and `UpdateCatalogSuperCategoryDto` both
 *  cap `slug` at 160 characters. */
const SLUG_MAX_LENGTH = 160;

/**
 * A slug the API will accept, derived from a category name.
 *
 * `slug` is REQUIRED on `CreateCatalogSuperCategoryTreeDto`, and the inline
 * "Add Subcategory" sent only `{ name, parentId }` — so every add came back 400
 * with "slug must be shorter than or equal to 160 characters, slug must be a
 * string". The second half of that message is the real one: the field was
 * missing, not too long.
 *
 * Two things this does that the shared `slugify` alone does not:
 *
 * - **Apostrophes are dropped, not dashed.** `slugify` collapses every run of
 *   non-alphanumerics into a dash, so `Men's` becomes `men-s`. Removing them
 *   first gives `mens`, which is what a reader expects a possessive to slug to.
 *   Fixed here rather than in `slugify` because that helper generates slugs
 *   across the app and changing it would move existing URLs.
 * - **The result is capped**, per the DTO.
 *
 * The local copy this replaces did neither, and turned spaces into dashes before
 * stripping the rest — so `Men's & Women's` came out `mens--womens`, two dashes
 * from the `&` that vanished between them, with no end-trimming either.
 */
export function superCategorySlug(name: string): string {
  const withoutApostrophes = name.replace(/['‘’]/g, "");
  // Trailing dash re-trimmed: the cut can land mid-separator.
  return slugify(withoutApostrophes).slice(0, SLUG_MAX_LENGTH).replace(/-+$/, "");
}
